// Унифицированный хук для работы с категориями согласно STYLEGUIDE.md

import { useEntityList, useEntityOperations } from '@/hooks/useEntity';
import { Category, DbCategory, CategoryCreate, CategoryUpdate, CategoryFilters, CategoryOption } from '@/types/entities/category';
import { dbCategoryToCategory, categoryCreateToDbCreate, categoryUpdateToDbUpdate } from '@/utils/mappers/categoryMappers';
import { TABLE_NAMES } from '@/constants/tableNames';
import { useMemo } from 'react';
import { supabaseWorker } from '@/integrations/supabase/workerClient';

/**
 * Унифицированный хук для работы с категориями
 * @param filters - фильтры для категорий
 * @returns объект с данными и операциями для категорий
 */
export function useCategoriesUnified(filters: CategoryFilters = {}) {
  // Базовый хук для получения списка категорий
  const {
    entities: dbCategories,
    isLoading,
    error
  } = useEntityList(TABLE_NAMES.CATEGORIES, dbCategoryToCategory);

  // Операции с категориями
  const operations = useEntityOperations(
    [TABLE_NAMES.CATEGORIES],
    // Create function
    async (categoryData: CategoryCreate) => {
      const { data, error } = await supabaseWorker
        .from(TABLE_NAMES.CATEGORIES)
        .insert(categoryData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    // Update function
    async (id: string, updates: CategoryUpdate) => {
      const { data, error } = await supabaseWorker
        .from(TABLE_NAMES.CATEGORIES)
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    // Delete function
    async (id: string) => {
      const { error } = await supabaseWorker
        .from(TABLE_NAMES.CATEGORIES)
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  );

  // Фильтрация категорий по типу (так как useEntityList не поддерживает фильтры)
  const categories = useMemo(() => {
    if (!dbCategories) return [];
    let filtered = dbCategories;
    
    if (filters.type) {
      filtered = filtered.filter(cat => cat.type === filters.type);
    }
    
    if (filters.parentId) {
      filtered = filtered.filter(cat => cat.parentId === filters.parentId);
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(cat => 
        cat.name.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [dbCategories, filters]);

  // Создание категории
  const createCategory = async (categoryData: Omit<Category, 'id' | 'userId' | 'createdAt' | 'updatedAt'>, userId: string): Promise<Category> => {
    const dbCreateData = categoryCreateToDbCreate(categoryData, userId);
    const createdDbCategory = await operations.create(dbCreateData);
    return dbCategoryToCategory(createdDbCategory);
  };

  // Обновление категории
  const updateCategory = async (id: string, categoryData: Partial<Omit<Category, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>): Promise<Category> => {
    const dbUpdateData = categoryUpdateToDbUpdate(categoryData);
    const updatedDbCategory = await operations.update({ id, updates: dbUpdateData });
    return dbCategoryToCategory(updatedDbCategory);
  };

  // Удаление категории
  const deleteCategory = async (id: string): Promise<void> => {
    await operations.remove(id);
  };

  // Получение опций для селектов, фильтруем пустые значения
  const categoryOptions = useMemo((): CategoryOption[] => {
    return categories
      .filter(category => category.id && category.id.trim() !== '' && category.name && category.name.trim() !== '')
      .map(category => ({
        value: category.id,
        label: category.name
      }));
  }, [categories]);

  // Получение опций с добавлением "Все категории"
  const categoryOptionsWithAll = useMemo((): CategoryOption[] => {
    return [
      { value: 'all', label: 'Все категории' },
      ...categoryOptions
    ];
  }, [categoryOptions]);

  return {
    // Данные
    categories,
    categoryOptions,
    categoryOptionsWithAll,
    loading: isLoading,
    error,
    
    // Операции
    createCategory,
    updateCategory,
    deleteCategory,
  };
}

/**
 * Хук для получения категорий инвентаря
 */
export function useInventoryCategories() {
  return useCategoriesUnified({ type: 'inventory' });
}

/**
 * Хук для получения категорий товаров магазина
 */
export function useStoreCategories() {
  return useCategoriesUnified({ type: 'store' });
}
